import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, Link2, Check, MessageCircle, Ghost, Send, Play } from "lucide-react";
import { music } from "@/lib/player";

const API = process.env.REACT_APP_BACKEND_URL;

const ytId = (url = "") => {
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|shorts\/))([\w-]{11})/);
    return m ? m[1] : null;
};

// Bouton « PARTAGER » pour chaque photo/vidéo :
// - partage natif (mobile) : envoie le MÉDIA direct (photo/vidéo) → collage en story
//   Instagram/Snapchat/WhatsApp ; repli sur le lien OG si l'app refuse le fichier
// - menu : WHATSAPP / SNAPCHAT (lien OG avec carte titre+image) / COPIER LE LIEN
// - clips YouTube : 2e bouton PLAY → lance le son dans le lecteur GLM radio
export default function ShareMediaButton({ kind = "photo", src, title, target, poster, clip, testid }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    const params = new URLSearchParams({ kind, src, title });
    if (target) params.set("target", target);
    if (poster) params.set("poster", poster);
    const shareUrl = `${API}/api/share/media?${params.toString()}`;
    const shareText = `${title} — GLOOM ENT.`;
    const clipId = ytId(clip) || ytId(src);
    // média local (non-YouTube) : on peut générer une story filigranée
    const localSrc = (poster || src).startsWith("/");
    const [loading, setLoading] = useState(false);

    // Partage STORY UNIQUEMENT : génère le média filigrané (vidéo→clip 9:16 / photo→image)
    // puis l'envoie à l'app choisie (Instagram/Snapchat/WhatsApp story) via le partage natif.
    const shareStory = async () => {
        setOpen(false);
        if (!localSrc) { setOpen((o) => !o); return; } // YouTube → menu lien
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/share/story?kind=${kind}&src=${encodeURIComponent(poster || src)}`);
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            const isVideo = blob.type.includes("mp4") || blob.type.includes("video");
            const ext = isVideo ? "mp4" : "jpg";
            const file = new File([blob], `${title.slice(0, 40)}.${ext}`, { type: blob.type });
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: shareText, text: shareText });
            } else {
                // desktop / pas de partage fichier → télécharge le média story
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = file.name;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 4000);
            }
        } catch { /* annulé */ } finally {
            setLoading(false);
        }
    };

    // clic principal : story (local) ; le lien OG reste dispo via le menu
    const share = () => {
        if (localSrc) shareStory();
        else setOpen((o) => !o);
    };

    const playClip = () => {
        if (clipId) music.playSpecific({ type: "yt", id: clipId, title });
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };

    const wa = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
    const snap = `https://www.snapchat.com/share?url=${encodeURIComponent(shareUrl)}`;

    return (
        <div ref={ref} className="relative inline-flex items-center gap-1.5">
            <button
                data-testid={testid}
                onClick={(e) => { e.stopPropagation(); share(); }}
                disabled={loading}
                aria-label="Partager en story"
                className="flex items-center gap-1.5 border border-white/15 bg-ink/70 px-2.5 py-1.5 font-mono text-[8px] tracking-[0.2em] text-cream/60 backdrop-blur-sm transition-all hover:border-violet hover:text-violet disabled:opacity-50"
            >
                <Share2 size={11} /> {loading ? "GÉNÉRATION…" : "STORY"}
            </button>
            {/* ouvre le menu lien (copier / WhatsApp / Snapchat) */}
            <button
                data-testid={`${testid}-link-btn`}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                aria-label="Copier le lien de partage"
                className="flex items-center border border-white/15 bg-ink/70 px-2 py-1.5 text-cream/60 backdrop-blur-sm transition-all hover:border-violet hover:text-violet"
            >
                <Link2 size={11} />
            </button>
            {clipId && (
                <button
                    data-testid={`${testid}-play`}
                    onClick={(e) => { e.stopPropagation(); playClip(); }}
                    aria-label={`Écouter ${title} sur GLM Radio`}
                    className="flex items-center gap-1.5 border border-blood/40 bg-ink/70 px-2.5 py-1.5 font-mono text-[8px] tracking-[0.2em] text-blood backdrop-blur-sm transition-all hover:border-blood hover:text-cream"
                >
                    <Play size={11} className="fill-blood" /> PLAY
                </button>
            )}
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid={`${testid}-menu`}
                        initial={{ opacity: 0, y: 6, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="absolute bottom-full right-0 z-[70] mb-2 w-52 border border-white/10 bg-ink/95 backdrop-blur-xl"
                    >
                        <button data-testid={`${testid}-native`} onClick={() => { setOpen(false); share(); }}
                            className="flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 font-mono text-[9px] tracking-[0.15em] text-cream/80 transition-colors hover:bg-violet/10">
                            <Send size={13} className="text-violet" /> STORY / AUTRES APPS
                        </button>
                        <a data-testid={`${testid}-whatsapp`} href={wa} target="_blank" rel="noreferrer"
                            className="flex items-center gap-3 border-b border-white/5 px-4 py-3 font-mono text-[9px] tracking-[0.15em] text-cream/80 transition-colors hover:bg-violet/10">
                            <MessageCircle size={13} className="text-green-400" /> WHATSAPP
                        </a>
                        <a data-testid={`${testid}-snapchat`} href={snap} target="_blank" rel="noreferrer"
                            className="flex items-center gap-3 border-b border-white/5 px-4 py-3 font-mono text-[9px] tracking-[0.15em] text-cream/80 transition-colors hover:bg-violet/10">
                            <Ghost size={13} className="text-yellow-300" /> SNAPCHAT
                        </a>
                        <button data-testid={`${testid}-copy`} onClick={copy}
                            className="flex w-full items-center gap-3 px-4 py-3 font-mono text-[9px] tracking-[0.15em] text-cream/80 transition-colors hover:bg-violet/10">
                            {copied ? <Check size={13} className="text-green-400" /> : <Link2 size={13} className="text-violet" />}
                            {copied ? "LIEN COPIÉ !" : "COPIER LE LIEN"}
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
